<!-- ConsentBanner.vue -->
<template>
  <div v-if="showBanner" class="consent-banner">
    <div class="consent-content">
      <div class="consent-text">
        <h3>{{ t('consent.title') }}</h3>
        <p>
          {{ t('consent.descriptionStart') }}
          <a :href="privacyPolicyUrl" target="_blank" class="privacy-link">
            {{ t('consent.privacyLinkText') }}
          </a>
          {{ t('consent.descriptionEnd') }}
        </p>
      </div>
      <div class="consent-actions">
        <button @click="handleAccept" class="consent-btn consent-btn-accept">
          {{ t('consent.accept') }}
        </button>
        <button @click="handleDecline" class="consent-btn consent-btn-decline">
          {{ t('consent.decline') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, defineEmits, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import type { SupportedLocale } from '@/i18n/types';

const { t, locale } = useI18n();
interface ConsentChoice {
  action: 'accept' | 'decline';
  timestamp: string;
}

const props = defineProps({
  privacyPolicyUrl: {
    type: String,
    default: ''
  }
});

const showBanner = ref(false);

const emit = defineEmits<{
  consentGiven: [choice: ConsentChoice];
}>();

const changeLanguage = (lang: SupportedLocale): void => {
  locale.value = lang
}

// Check if we should show the banner
const checkShouldShowBanner = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const appOptIn = urlParams.get('trackingOptIn');
  const appLocale = urlParams.get('locale');
  if (appLocale && ['en', 'es', 'de'].includes(appLocale)) {
    locale.value = appLocale as SupportedLocale
  }

  if(!window.ReactNativeWebView) {
    // If not in WebView, check local storage
    const storedOptIn = localStorage.getItem('trackingOptIn');
    return storedOptIn === null || storedOptIn === 'undefined' || storedOptIn === 'null';
  }
  
  // If appOptIn is null or undefined, user hasn't made a choice yet
  // If it's 'false', user has explicitly declined
  // If it's 'true', user has accepted
  return appOptIn === null || appOptIn === 'undefined' || appOptIn === 'null';
};

const handleAccept = () => {
  const choice: ConsentChoice = {
    action: 'accept',
    timestamp: new Date().toISOString()
  };
  
  // Set local storage to remember user's choice
  localStorage.setItem('trackingOptIn', 'true');

  // Send message to React Native
  sendConsentToApp(choice);
  
  // Hide banner
  showBanner.value = false;
  
  // Emit event for parent component
  emit('consentGiven', choice);
};

const handleDecline = () => {
  const choice: ConsentChoice = {
    action: 'decline',
    timestamp: new Date().toISOString()
  };

  // Set local storage to remember user's choice
  localStorage.setItem('trackingOptIn', 'false');
  
  // Send message to React Native
  sendConsentToApp(choice);
  
  // Hide banner
  showBanner.value = false;
  
  // Emit event for parent component
  emit('consentGiven', choice);
};

const sendConsentToApp = (choice: ConsentChoice) => {
  // Send message to React Native WebView
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({
      type: 'CONSENT_CHOICE',
      payload: choice
    }));
  }
};

onMounted(() => {
  // Small delay to ensure URL params are loaded
  setTimeout(() => {
    showBanner.value = checkShouldShowBanner();
  }, 100);
});
</script>

<style scoped>
.consent-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  z-index: 10000;
  padding: 20px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
}

.consent-content {
  max-width: 600px;
  margin: 0 auto;
}

.consent-text h3 {
  margin: 0 0 10px 0;
  font-size: 18px;
  font-weight: 600;
}

.consent-text p {
  margin: 0 0 20px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #e0e0e0;
}

.consent-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.consent-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.consent-btn-accept {
  background: #1877f2;
  color: white;
}

.consent-btn-accept:hover {
  background: #166fe5;
}

.consent-btn-decline {
  background: transparent;
  color: #e0e0e0;
  border: 1px solid #555;
}

.consent-btn-decline:hover {
  background: #333;
  border-color: #777;
}

.privacy-link-text {
  margin-bottom: 20px !important;
}

.privacy-link {
  color: #4da6ff;
  text-decoration: underline;
}

.privacy-link:hover {
  color: #66b3ff;
}

@media (max-width: 480px) {
  .consent-actions {
    flex-direction: column;
  }

  .consent-btn {
    width: 100%;
  }
}
</style>